import { Minus, Plus } from "lucide-react";
import { useState, type CSSProperties } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  approximatePixels,
  parseMarginSides,
  serializeMarginSides,
  steppedLength,
  type MarginSide,
  type MarginSides,
} from "./marginSides";
import { withDefaultPxUnits } from "./styleLength";

/** The diagram inset a very large margin converges on, in diagram pixels. */
const MAX_DIAGRAM_OFFSET = 20;

/** The authored length that draws at half the maximum inset. */
const HALF_SCALE_PIXELS = 24;

/** Assistive-tech and hover labels per side. */
const SIDE_LABELS: Record<MarginSide, string> = {
  top: "Top margin",
  right: "Right margin",
  bottom: "Bottom margin",
  left: "Left margin",
};

/** The content box edge that lights up while a side is being worked on. */
const ACTIVE_EDGE_CLASS: Record<MarginSide, string> = {
  top: "border-t-primary",
  right: "border-r-primary",
  bottom: "border-b-primary",
  left: "border-l-primary",
};

interface MarginInputProps {
  /** Knob label. */
  label: string;
  /** Current authored margin shorthand ("" when unset). */
  value: string;
  /** Shorthand hinting what the renderer falls back to, shown per side. */
  placeholder?: string;
  /** Commits the new shorthand ("" clears the property). */
  onCommit: (value: string) => void;
}

/**
 * Visual margin knob: four steppers around a diagram of the block, whose
 * content rectangle moves live as a side is typed, arrow-stepped or clicked and
 * whose worked-on edge lights up. Bare numbers default to px and a blank side
 * stands in as 0, so emptying all four clears the property. Typing commits on
 * blur or Enter while a stepper button commits on the click, and the control
 * resyncs on the authored value rather than needing to be remounted, so
 * committing one side never drops the focused one.
 * @param label - Knob label
 * @param value - Current authored margin shorthand
 * @param placeholder - Shorthand hinting the renderer's fallback
 * @param onCommit - Commits the new shorthand
 * @returns The margin control
 */
export function MarginInput({
  label,
  value,
  placeholder,
  onCommit,
}: MarginInputProps) {
  const [draft, setDraft] = useState(() => ({
    source: value,
    sides: parseMarginSides(value),
  }));
  const [focused, setFocused] = useState<MarginSide | null>(null);
  const [hovered, setHovered] = useState<MarginSide | null>(null);
  // The authored value wins as soon as it moves on from what the draft was
  // typed against, so a commit or an undo flows back in on its own.
  const sides = draft.source === value ? draft.sides : parseMarginSides(value);
  const hints = parseMarginSides(placeholder ?? "");
  const active = focused ?? hovered;
  const offsets: Record<MarginSide, number> = {
    top: diagramOffset(sides.top || hints.top),
    right: diagramOffset(sides.right || hints.right),
    bottom: diagramOffset(sides.bottom || hints.bottom),
    left: diagramOffset(sides.left || hints.left),
  };

  const setSide = (side: MarginSide, next: string) =>
    setDraft({ source: value, sides: { ...sides, [side]: next } });

  // Every side is normalized on the way out, so a side left dirty by a click on
  // a different side commits as what it reads rather than as raw typing.
  const commitSides = (next: MarginSides) => {
    const normalized: MarginSides = {
      top: withDefaultPxUnits(next.top),
      right: withDefaultPxUnits(next.right),
      bottom: withDefaultPxUnits(next.bottom),
      left: withDefaultPxUnits(next.left),
    };
    setDraft({ source: value, sides: normalized });
    const shorthand = serializeMarginSides(normalized);
    if (shorthand !== value) {
      onCommit(shorthand);
    }
  };

  const stepButton = (side: MarginSide, steps: number) => (
    <button
      type="button"
      className="flex w-[18px] shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground focus-visible:outline-none active:bg-muted"
      aria-label={`${steps > 0 ? "Increase" : "Decrease"} ${side} margin`}
      title={`${steps > 0 ? "Increase" : "Decrease"} ${side} margin`}
      onFocus={() => setFocused(side)}
      onBlur={() => setFocused(null)}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() =>
        commitSides({
          ...sides,
          [side]: steppedLength(sides[side] || hints[side] || "0", steps),
        })
      }
    >
      {steps > 0 ? <Plus className="size-3" /> : <Minus className="size-3" />}
    </button>
  );

  const sideStepper = (side: MarginSide, placement: string) => (
    <div
      className={cn(
        "flex h-7 w-[4.5rem] items-stretch overflow-hidden rounded-md border border-input bg-transparent shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
        placement,
      )}
      onMouseEnter={() => setHovered(side)}
      onMouseLeave={() => setHovered(null)}
    >
      {stepButton(side, -1)}
      <Input
        className="h-full min-w-0 flex-1 rounded-none border-0 px-0 text-center text-[11px] tabular-nums shadow-none focus-visible:ring-0 md:text-[11px]"
        aria-label={SIDE_LABELS[side]}
        title={SIDE_LABELS[side]}
        value={sides[side]}
        placeholder={hints[side] === "" ? "0" : hints[side]}
        onFocus={() => setFocused(side)}
        onChange={(event) => setSide(side, event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
            return;
          }
          if (event.key === "Escape") {
            setSide(side, parseMarginSides(value)[side]);
            return;
          }
          if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
            return;
          }
          event.preventDefault();
          const steps =
            (event.key === "ArrowUp" ? 1 : -1) * (event.shiftKey ? 10 : 1);
          setSide(
            side,
            steppedLength(sides[side] || hints[side] || "0", steps),
          );
        }}
        onBlur={(event) => {
          setFocused(null);
          commitSides({ ...sides, [side]: event.target.value });
        }}
      />
      {stepButton(side, 1)}
    </div>
  );

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Label className="text-[11px]">{label}</Label>
        {value !== "" && (
          <button
            type="button"
            className="rounded-sm text-[10px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            title="Remove the authored margin"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onCommit("")}
          >
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-[4.5rem_1fr_4.5rem] items-center justify-items-center gap-1">
        {sideStepper("top", "col-start-2 row-start-1")}
        {sideStepper("left", "col-start-1 row-start-2")}
        <div
          aria-hidden="true"
          className="relative col-start-2 row-start-2 h-[72px] w-full overflow-hidden rounded-md border border-dashed border-border bg-muted/50"
        >
          {active !== null && (
            <div
              className="absolute bg-primary/15 transition-all duration-200 ease-out motion-reduce:transition-none"
              style={bandStyle(active, offsets)}
            />
          )}
          <div
            className={cn(
              "absolute rounded-sm border border-border bg-background shadow-sm transition-all duration-200 ease-out motion-reduce:transition-none",
              active !== null && ACTIVE_EDGE_CLASS[active],
            )}
            style={{
              top: offsets.top,
              right: offsets.right,
              bottom: offsets.bottom,
              left: offsets.left,
            }}
          >
            <div className="flex h-full flex-col items-center justify-center gap-1 overflow-hidden px-2">
              <span className="h-[3px] w-full rounded-full bg-muted-foreground/25" />
              <span className="h-[3px] w-2/3 rounded-full bg-muted-foreground/20" />
            </div>
          </div>
        </div>
        {sideStepper("right", "col-start-3 row-start-2")}
        {sideStepper("bottom", "col-start-2 row-start-3")}
      </div>
    </div>
  );
}

/**
 * The inset a side draws at, on a curve that keeps small margins legible and
 * lets large ones converge rather than swallow the content box.
 * @param side - The authored side value
 * @returns The inset in diagram pixels
 */
function diagramOffset(side: string): number {
  const pixels = Math.abs(approximatePixels(side));
  return Math.round(
    (MAX_DIAGRAM_OFFSET * pixels) / (pixels + HALF_SCALE_PIXELS),
  );
}

/**
 * The geometry of the tinted band standing for one side's margin zone.
 * @param side - The side being worked on
 * @param offsets - The inset each side draws at
 * @returns The band's absolute position
 */
function bandStyle(
  side: MarginSide,
  offsets: Record<MarginSide, number>,
): CSSProperties {
  switch (side) {
    case "top":
      return { top: 0, left: 0, right: 0, height: offsets.top };
    case "right":
      return { top: 0, right: 0, bottom: 0, width: offsets.right };
    case "bottom":
      return { bottom: 0, left: 0, right: 0, height: offsets.bottom };
    case "left":
      return { top: 0, bottom: 0, left: 0, width: offsets.left };
  }
}
